import React from 'react';

import {
  AssociateSoftwareTokenCommand,
  AuthFlowType,
  ChallengeNameType,
  CognitoIdentityProviderClient,
  GlobalSignOutCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  SetUserMFAPreferenceCommand,
  VerifySoftwareTokenCommand,
  VerifySoftwareTokenResponseType,
} from '@aws-sdk/client-cognito-identity-provider';

import { CognitoProviderContext, CognitoProviderContextValue } from './CognitoProviderContext';
import {
  MfaGenerateTotpUriKeyParams,
  MfaGenerateTotpUriKeyResult,
  MfaSetupParams,
  MfaValidateSetupUserCodeParams,
  SignInParams,
  SignInResult,
  SignInWithSsoParams,
  SignOutParams,
} from './types';
import { ssoTokenResponseSchema } from './validation';

type CognitoProviderProps = {
  region: string | undefined;
  clientId: string | undefined;
  endpoint?: string;
};

export const CognitoProvider: React.FC<React.PropsWithChildren<CognitoProviderProps>> = ({
  region,
  clientId,
  endpoint,
  children,
}) => {
  const cognitoClient = React.useRef<CognitoIdentityProviderClient | null>(null);

  // https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
  if (!cognitoClient.current) {
    cognitoClient.current = new CognitoIdentityProviderClient({ region, endpoint });
  }

  const signIn = React.useCallback(
    async ({ username, password, session, userCode }: SignInParams): Promise<SignInResult> => {
      if (!cognitoClient.current) {
        throw new Error('Cognito client is not configured');
      }

      if (password) {
        const command = new InitiateAuthCommand({
          AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
          ClientId: clientId,
          AuthParameters: {
            USERNAME: username,
            PASSWORD: password,
          },
        });
        const { AuthenticationResult, Session } = await cognitoClient.current.send(command);
        const idToken = AuthenticationResult?.IdToken;
        const accessToken = AuthenticationResult?.AccessToken;
        const refreshToken = AuthenticationResult?.RefreshToken;

        return { accessToken, refreshToken, idToken, session: Session };
      }

      if (session && userCode) {
        const command = new RespondToAuthChallengeCommand({
          Session: session,
          ClientId: clientId,
          ChallengeName: ChallengeNameType.SOFTWARE_TOKEN_MFA,
          ChallengeResponses: {
            USERNAME: username,
            SOFTWARE_TOKEN_MFA_CODE: userCode,
          },
        });

        const { AuthenticationResult } = await cognitoClient.current.send(command);
        if (!AuthenticationResult?.AccessToken) {
          throw new Error('Unauthorized');
        }

        return {
          accessToken: AuthenticationResult.AccessToken,
          refreshToken: AuthenticationResult.RefreshToken,
          idToken: AuthenticationResult.IdToken,
          session,
        };
      }

      throw new Error('Unauthorized');
    },
    [clientId],
  );

  const signInWithSso = React.useCallback(
    async ({ code, ssoHost, redirectUrl }: SignInWithSsoParams): Promise<SignInResult> => {
      if (!clientId) {
        throw new Error('Cognito client is not configured');
      }

      // https://docs.aws.amazon.com/cognito/latest/developerguide/token-endpoint.html
      const response = await fetch(new URL('/oauth2/token', ssoHost), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          redirect_uri: redirectUrl,
        }),
      });

      if (!response.ok) {
        throw new Error('Unauthorized');
      }

      const tokens = ssoTokenResponseSchema.parse(await response.json());

      return {
        idToken: tokens.id_token,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
      };
    },
    [clientId],
  );

  const signOut = React.useCallback(async ({ accessToken }: SignOutParams) => {
    if (!cognitoClient.current) {
      throw new Error('Cognito client is not configured');
    }

    const command = new GlobalSignOutCommand({
      AccessToken: accessToken,
    });

    await cognitoClient.current.send(command);
  }, []);

  const mfaGenerateTotpUriKey = React.useCallback(
    async ({
      email,
      accessToken,
    }: MfaGenerateTotpUriKeyParams): Promise<MfaGenerateTotpUriKeyResult> => {
      if (!cognitoClient.current) {
        throw new Error('Cognito client is not configured');
      }

      const command = new AssociateSoftwareTokenCommand({
        AccessToken: accessToken,
      });
      const { SecretCode } = await cognitoClient.current.send(command);

      if (!SecretCode) {
        throw new Error('Invalid token');
      }

      // https://github.com/google/google-authenticator/wiki/Key-Uri-Format
      const uriKey = encodeURI(
        `otpauth://totp/Clear Verify:${email}?secret=${SecretCode}&issuer=Cognito`,
      );

      return { uriKey };
    },
    [],
  );

  const mfaValidateSetupUserCode = React.useCallback(
    async ({ userCode, accessToken }: MfaValidateSetupUserCodeParams) => {
      if (!cognitoClient.current) {
        throw new Error('Cognito client is not configured');
      }

      const command = new VerifySoftwareTokenCommand({
        AccessToken: accessToken,
        UserCode: userCode,
      });
      const { Status } = await cognitoClient.current.send(command);

      if (Status === VerifySoftwareTokenResponseType.ERROR) {
        throw new Error('Invalid token');
      }
    },
    [],
  );

  const mfaSetup = React.useCallback(async ({ accessToken }: MfaSetupParams) => {
    if (!cognitoClient.current) {
      throw new Error('Cognito client is not configured');
    }

    const command = new SetUserMFAPreferenceCommand({
      AccessToken: accessToken,
      SoftwareTokenMfaSettings: {
        Enabled: true,
        PreferredMfa: true,
      },
    });

    await cognitoClient.current.send(command);
  }, []);

  const contextValue: CognitoProviderContextValue = React.useMemo(() => {
    return {
      cognitoClient: cognitoClient.current,
      signIn,
      signInWithSso,
      signOut,
      mfaGenerateTotpUriKey,
      mfaValidateSetupUserCode,
      mfaSetup,
    };
  }, [signIn, signInWithSso, signOut, mfaGenerateTotpUriKey, mfaValidateSetupUserCode, mfaSetup]);

  return (
    <CognitoProviderContext.Provider value={contextValue}>
      {children}
    </CognitoProviderContext.Provider>
  );
};
